import { API } from "./api";
import { fetchUsagePreference, subscribe } from "./usagePreference";
import { USAGE_NAV_VIA, USAGE_SCREENS, USAGE_TABS, isAllowedClientEvent } from "./usageVocabulary";

/**
 * The first-party usage tracker (#1110, analytics epic #1114, D218).
 *
 * The only file in the frontend that talks to the usage endpoint, and the
 * guard in `api/test/usage.test.ts` fails if a second one does. What it may
 * say is closed by `usageVocabulary.js`: a screen TEMPLATE, a tab, an item and
 * a navigation source, every one a word from a list. There is no field for a
 * path, a query string, a search term, an id or a name, so none can be sent.
 *
 * WHAT IT NEVER DOES. It writes nothing to the browser: no cookie, no
 * storage of any kind, nothing that survives a reload. It reads no full URL,
 * no referrer and no browser identification. It makes one kind of request, to
 * this origin. It logs nothing, even when a send fails.
 *
 * WHEN IT SENDS. Only after the server has said this account is counted
 * (`GET /api/usage/preference`, `counted: true`). That is false when the
 * server's switch is off, for an admin, and for an opted-out account. Until
 * the answer arrives events wait in memory and are never sent. A change of the
 * preference (the switch in Settings) is heard at once through `subscribe`:
 * off clears the queue, and a signed-out page stops the tracker entirely.
 *
 * HOW IT SENDS. A JSON `fetch` with `keepalive`, never the beacon API: the
 * server's CSRF guard refuses a beacon's `text/plain` body. The browser adds
 * the origin headers the guard reads. A failed send is dropped. There are no
 * retries, because a retry needs a place to keep the batch.
 */

/** Flush when this many events are waiting. */
const FLUSH_AT = 20;
/** The server takes 1..50 events per request. */
const MAX_BATCH = 50;
/** The server refuses `ago_ms` above ten minutes, so older events are dropped. */
const MAX_AGE_MS = 10 * 60 * 1000;
const FLUSH_EVERY_MS = 30 * 1000;
const RATE_PAUSE_MS = 10 * 60 * 1000;
/** Waiting events are capped, so a page that never learns the answer cannot grow without bound. */
const MAX_QUEUE = 200;
/** A navigation source that no screen change claims within this time is stale. */
const NAV_MARK_TTL_MS = 3000;

/**
 * #1594: a sign-up just happened in this page. `signup.done` is sent once, on the first counted start
 * after it (an account must exist to have a subject). Memory only: a reload before the tracker is on
 * forgets it, which is a missed count, never a stored one.
 */
let signupPending = false;

/** "off" until started; "unknown" until the server answers; then "on" or "off". */
let state = "off";
let queue = [];
let pausedUntil = 0;
let generation = 0;
let unsubscribe = null;
let timer = null;
let pendingNav = null;

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

function stripTrailingSlash(pathname) {
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

function matchesTemplate(template, segments) {
  const parts = template.split("/").slice(1);
  if (parts.length !== segments.length) return false;
  return parts.every((part, i) => (part.startsWith(":") ? segments[i] !== "" : part === segments[i]));
}

/**
 * The route template for a pathname, or null. A `:param` segment matches any
 * one segment. Null means nothing is sent: a raw path is never returned.
 */
export function screenOf(pathname) {
  if (typeof pathname !== "string" || !pathname.startsWith("/")) return null;
  const segments = stripTrailingSlash(pathname).split("/").slice(1);
  // Exact templates first, so a literal route wins over a parameter route.
  const literal = USAGE_SCREENS.find((t) => !t.includes(":") && matchesTemplate(t, segments));
  if (literal) return literal;
  return USAGE_SCREENS.find((t) => t.includes(":") && matchesTemplate(t, segments)) ?? null;
}

/**
 * The `tab` parameter (on `/learn`, the section or topic, #1128), only when it is one of this screen's own tabs. The
 * query string is read for that single parameter and is never kept or sent.
 */
export function tabOf(screen, search) {
  if (typeof screen !== "string" || typeof search !== "string") return null;
  if (!Object.prototype.hasOwnProperty.call(USAGE_TABS, screen)) return null;
  let value;
  try {
    const params = new URLSearchParams(search);
    // #1594: two screens carry their step in `?step=`. Onboarding's is the word itself; Team selection
    // reads `step=plan` as `notes` and an open game (`?game=`, whose id is never kept) as `pick`.
    if (screen === "/onboarding") value = params.get("step");
    else if (screen === "/coach-portal/team") {
      if (params.get("game") === null) return null;
      value = params.get("step") === "plan" ? "notes" : "pick";
    } else value = params.get("tab");
    // #1128: `/learn` is two sections now. A topic view reports its topic (the
    // same word the old tab was), My training and the Guides list report the
    // section. `section=` wins over a leftover `tab=`, as the page reads it.
    if (screen === "/learn" && params.get("section") !== null) {
      const section = params.get("section");
      const topic = params.get("topic");
      value = section === "guides" && topic !== null ? topic : section;
    }
  } catch {
    return null;
  }
  return value !== null && USAGE_TABS[screen].includes(value) ? value : null;
}

/* -------------------------------------------------------------------------- */
/* Navigation source                                                          */
/* -------------------------------------------------------------------------- */

/** Called when registration succeeds, before the new account's session is set. */
export function markSignup() {
  signupPending = true;
}

/** Remember how the next screen change was reached. Used once, then cleared. */
export function markNav(via) {
  if (typeof via !== "string" || !USAGE_NAV_VIA.includes(via)) return;
  pendingNav = { via, at: Date.now() };
}

/** The remembered source, if it is still fresh. Clears it either way. */
export function takeNav() {
  const mark = pendingNav;
  pendingNav = null;
  if (!mark || Date.now() - mark.at > NAV_MARK_TTL_MS) return undefined;
  return mark.via;
}

/* -------------------------------------------------------------------------- */
/* The queue                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Queue one event. Dropped unless it passes the same rules as the server's
 * check, and unless counting is on or still being asked about. Never throws.
 * Only the five vocabulary fields are read from `fields`.
 */
export function track(name, fields = {}) {
  try {
    if (state === "off" || Date.now() < pausedUntil) return;
    const event = { name };
    for (const key of ["screen", "tab", "item", "via"]) {
      const value = fields ? fields[key] : undefined;
      if (value !== undefined && value !== null) event[key] = value;
    }
    if (!isAllowedClientEvent(event)) return;
    queue.push({ ...event, t: Date.now() });
    if (queue.length > MAX_QUEUE) queue = queue.slice(queue.length - MAX_QUEUE);
    if (state === "on" && queue.length >= FLUSH_AT) flush();
  } catch {
    // Telemetry never breaks the page.
  }
}

function clearQueue() {
  queue = [];
}

async function send(batch) {
  const now = Date.now();
  const events = batch.map(({ t, ...event }) => ({ ...event, ago_ms: now - t }));
  let res;
  try {
    res = await fetch(`${API}/usage/events`, {
      method: "POST",
      keepalive: true,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
    });
  } catch {
    return; // Network error: the batch is dropped.
  }
  if (res.status === 204) return;
  if (res.status === 429) {
    pausedUntil = Date.now() + RATE_PAUSE_MS;
    clearQueue();
    return;
  }
  if (res.status === 403 || res.status === 404) {
    let code = null;
    try {
      code = (await res.json())?.code ?? null;
    } catch {
      code = null;
    }
    if ((res.status === 403 && code === "usage_opted_out") || (res.status === 404 && code === "usage_off")) {
      // Stop until the preference changes (a `publish` brings it back).
      turnOff();
    }
  }
  // Anything else: the batch is dropped.
}

/** Send what is waiting, at most 50 per request. A no-op unless counting is on. */
export function flush() {
  if (state !== "on" || Date.now() < pausedUntil || queue.length === 0) return;
  const cutoff = Date.now() - MAX_AGE_MS;
  const fresh = queue.filter((e) => e.t >= cutoff);
  clearQueue();
  for (let i = 0; i < fresh.length; i += MAX_BATCH) {
    send(fresh.slice(i, i + MAX_BATCH));
  }
}

/* -------------------------------------------------------------------------- */
/* Lifecycle                                                                  */
/* -------------------------------------------------------------------------- */

function onVisibility() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") flush();
}

function onPageHide() {
  flush();
}

function onTick() {
  if (typeof document === "undefined" || document.visibilityState === "visible") flush();
}

function turnOn() {
  if (state === "on") return;
  state = "on";
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
  if (typeof window !== "undefined") window.addEventListener("pagehide", onPageHide);
  timer = setInterval(onTick, FLUSH_EVERY_MS);
}

function turnOff() {
  state = "off";
  clearQueue();
  detach();
}

function detach() {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
  if (typeof window !== "undefined") window.removeEventListener("pagehide", onPageHide);
}

function applyPreference(pref) {
  if (pref && pref.counted === true) {
    turnOn();
    if (signupPending) {
      signupPending = false;
      track("signup.done");
    }
  } else {
    // Not counted: the sign-up is forgotten, not kept for a later opt-in.
    signupPending = false;
    turnOff();
  }
}

/**
 * Begin tracking for a signed-in player or coach. Asks the server whether
 * this account is counted, and listens for the switch changing. Returns the
 * stop function.
 */
export function startUsageTracking() {
  stopUsageTracking();
  const mine = ++generation;
  state = "unknown";
  unsubscribe = subscribe((pref) => {
    if (mine === generation) applyPreference(pref);
  });
  Promise.resolve()
    .then(() => fetchUsagePreference())
    .then((pref) => {
      if (mine === generation && state === "unknown") applyPreference(pref);
    })
    .catch(() => {
      if (mine === generation && state === "unknown") {
        signupPending = false;
        turnOff();
      }
    });
  return stopUsageTracking;
}

/** Stop, clear the queue and forget everything. Signed out, or the page is going away. */
export function stopUsageTracking() {
  generation += 1;
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  turnOff();
  pendingNav = null;
  pausedUntil = 0;
}
